import { useAuth, useLoginWithRedirect } from '@frontegg/react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Outlet, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useAuthActions } from '@frontegg/react';
import { useQueryClient } from '@tanstack/react-query';
import { storeInLocal } from 'src/lib/storage';
import {
  isEmbeddedEnvironment,
  isVimOAuthFlow,
  useEmbeddedAuthActions,
  useVimAuthActions,
} from 'src/hooks/embedded/login';
import { ErrorScreen } from 'src/components/errorScreen';
import {
  getCurrentPractitioner,
  getCurrentPractitionerEntitlements,
  getCurrentPractitionerSettings,
} from 'src/store/actions/practitioner.action';
import { authenticationStatus } from 'src/store/slices/auth.slice';
import { AppLoader } from 'src/components/AppLoader';
import { useFavicon, useHtmlTitle, useTenant } from 'src/hooks/misc';
import {
  getQuotaAction,
  getTimeQuotaAction,
} from 'src/store/actions/quota.action';
import { getLegalConfirmationAction } from 'src/store/actions/legalConfirmation.action';
import { analytics, RUM, salesforceChat } from 'src/services';
import { getPreBuiltAdminTemplates } from 'src/modules/ambient_solo/store/actions/settings';
import { Onboarding } from 'src/pages/onboarding';

export const ProtectedRoute = () => {
  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { user, isAuthenticated } = useAuth();
  const { loadEntitlements } = useAuthActions();
  const loginWithRedirect = useLoginWithRedirect();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [entitlementsLoaded, setEntitlementsLoaded] = useState(false);
  const {
    currentPractitionerLoading,
    currentPractitioner,
    currentPractitionerEntitlements,
    currentPractitionerEntitlementsLoading,
    currentPractitionerSettings,
  } = useSelector((state) => state?.practitionerState);

  useLayoutEffect(() => {
    queryClient.clear();
  }, [user?.tenantId]);

  const tenant = useTenant();

  useFavicon({ tenant });
  useHtmlTitle({ tenant });

  const { embeddedLogin, error: embeddedError } = useEmbeddedAuthActions();
  const { vimLogin } = useVimAuthActions();

  useEffect(() => {
    const redirectUrl = localStorage.getItem('_REDIRECT_AFTER_LOGIN_');
    if (isAuthenticated && redirectUrl) {
      localStorage.removeItem('_REDIRECT_AFTER_LOGIN_');
      navigate(redirectUrl);
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem(
        '_REDIRECT_AFTER_LOGIN_',
        location.pathname + location.search
      );

      if (isVimOAuthFlow()) {
        vimLogin();
      } else if (isEmbeddedEnvironment()) {
        embeddedLogin();
      } else {
        loginWithRedirect();
      }
    } else {
      loadEntitlements({
        callback: () => {
          setEntitlementsLoaded(true);
        },
      });
      dispatch(getCurrentPractitioner());
      dispatch(getCurrentPractitionerSettings());
      dispatch(getCurrentPractitionerEntitlements());
      dispatch(authenticationStatus(true));
      dispatch(getQuotaAction());
      dispatch(getTimeQuotaAction());
      dispatch(getPreBuiltAdminTemplates());
      storeInLocal(`user_name`, user?.name);
      storeInLocal(`user_email`, user?.email);
      storeInLocal(`user_id`, user?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (tenant === 'OP') {
      dispatch(getLegalConfirmationAction());
    }
  }, [tenant]);

  useEffect(() => {
    if (
      isAuthenticated &&
      user?.id &&
      user?.name &&
      user?.email &&
      !currentPractitionerEntitlementsLoading
    ) {
      RUM.setUser({
        id: user.id,
        name: user.name,
        email: user.email,
        plan: currentPractitionerEntitlements?.plan_type || 'n/a',
      });
    }
  }, [
    isAuthenticated,
    user?.id,
    user?.name,
    user?.email,
    currentPractitionerEntitlementsLoading,
  ]);

  useEffect(() => {
    if (
      currentPractitioner &&
      currentPractitionerEntitlements &&
      isAuthenticated
    ) {
      const {
        org_name,
        intercom,
        practitioner_name,
        practitioner_first_name,
        practitioner_last_name,
      } = currentPractitioner;

      window.Intercom?.('update', {
        hide_default_launcher: !!intercom?.hide_default_launcher,
      });

      if (tenant === 'OP') {
        salesforceChat.init();
      }

      const integrations = {
        integrations: {
          Intercom:
            import.meta.env.VITE_ENABLE_INTERCOM_USER_HASH === 'true'
              ? intercom
              : {},
        },
      };
      analytics.identify(
        user?.id,
        {
          email: user?.email,
          name: user?.name,
          organization: org_name,
          full_name: practitioner_name,
          first_name: practitioner_first_name,
          last_name: practitioner_last_name,
          plan_type: currentPractitionerEntitlements.plan_type,
          company: {
            name: org_name,
            plan_type: currentPractitionerEntitlements.plan_type,
          },
        },
        integrations
      );
    }
  }, [
    currentPractitioner,
    isAuthenticated,
    user?.email,
    user?.id,
    user?.name,
    currentPractitionerEntitlements,
  ]);

  if (embeddedError) {
    return (
      <ErrorScreen
        className="h-screen"
        title={embeddedError.title}
        description={embeddedError.message}
      />
    );
  }

  return isAuthenticated &&
    !currentPractitionerLoading &&
    entitlementsLoaded ? (
    settingsOrgValue?.onboarding_complete != undefined &&
    !settingsOrgValue?.onboarding_complete ? (
      <>
        {/* Navigate to onboarding just for representation */}
        <Navigate to="/onboarding" />
        <Onboarding />
      </>
    ) : (
      <Outlet />
    )
  ) : (
    <AppLoader />
  );
};
